
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { forkJoin, Observable } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { FieldOptionObj } from 'ah-common-lib/src/form/interfaces';
import TagMultiSelect from './TagMultiSelect.vue';
import WithRequestManager from 'ah-common-lib/src/requestManager/WithRequestManager.vue';
import { ListSortParams, PaginatedQuery, PaginatedResponse } from 'ah-api-gateways';
import { debounce, DebouncedFunc, isEqual, uniqBy } from 'lodash';

/**
 * Searchable Multi Select Component
 *
 * Allows the user to search for a specific entity in a selector
 */
@Component({ components: { TagMultiSelect } })
export default class SearchableSelector<T = any> extends Mixins(WithRequestManager) {
  /**
   * Paginated fetch request
   *
   * Used to fetch the data to populate the select options
   */
  @Prop({ default: () => () => null }) fetchRequest!: (query: any) => Observable<PaginatedResponse<T>>;

  /**
   * Single entity fetch request
   *
   * Used to fetch the initial unkown data
   */
  @Prop({ default: () => () => null }) singleFetchRequest!: (query: any) => Observable<T>;

  /**
   * Selected values.
   *
   * Syncable with v-model
   */
  @Prop({ default: () => [] }) value!: any[];

  /**
   * List request pagesize
   *
   * Defaults to 20
   */
  @Prop({ default: 20 }) pageSize!: number;

  /**
   * Search by key in pagination request
   *
   * Defaults to 'query'
   */
  @Prop({ default: 'query' }) searchBy!: string;

  /**
   * Entity value key
   *
   * Defaults to 'id'
   */
  @Prop({ default: 'id' }) valueKey!: string;

  /**
   * Entity label key
   *
   * Defaults to 'name'
   */
  @Prop({ default: 'name' }) valueLabel!: string | ((item: T) => string);

  /**
   * Entity sort key
   */
  @Prop({ required: false }) sortKey?: string | string[];

  private options: FieldOptionObj<T>[] = [];

  private debouncedSearch!: DebouncedFunc<() => void>;
  private searchTerm: string = '';

  private lastResult: PaginatedResponse<T> | null = null;

  created() {
    this.debouncedSearch = debounce(() => this.fetchDataRequest().subscribe(), 600);
  }

  get hasMoreItems() {
    if (
      typeof this.lastResult?.pageNumber !== 'number' ||
      typeof this.lastResult?.pageSize !== 'number' ||
      typeof this.lastResult?.total !== 'number'
    ) {
      return false;
    }
    return (this.lastResult.pageNumber + 1) * this.lastResult.pageSize < this.lastResult.total;
  }

  get loading() {
    return this.requestManager.requestStates['fetchData'] === 'pending';
  }

  get unkownValues() {
    return this.value.filter((v) => !this.options.find((opt) => isEqual(opt.value, v)));
  }

  get selectedOptions() {
    return this.options.filter((opt) => this.value.find((val) => isEqual(val, opt.value)));
  }

  private onTagSearch(search: string) {
    this.searchTerm = search;
    this.debouncedSearch();
  }

  private getLabelString(item: any) {
    if (typeof this.valueLabel === 'string') {
      return item[this.valueLabel];
    }
    return this.valueLabel(item);
  }

  @Watch('fetchRequest', { immediate: true })
  onFetchRequestChange() {
    this.fetchDataRequest().subscribe();
  }

  @Watch('unkownValues')
  onUnkownValuesChange() {
    if (this.unkownValues.length > 0) {
      this.fetchDataRequest().subscribe();
    }
  }

  @Watch('selectedOptions', { immediate: true })
  onSelectedOptionsChange() {
    this.$emit('update:selectedOptions', this.selectedOptions);
  }

  get sortParams(): ListSortParams {
    if (this.sortKey) {
      return {
        sort: this.sortKey,
        sortDirection: 'ASC',
      };
    }
    if (typeof this.valueLabel === 'string') {
      return {
        sort: this.valueLabel,
        sortDirection: 'ASC',
      };
    }
    return {};
  }

  onInfiniteScroll(intersection: IntersectionObserverEntry) {
    if (intersection.isIntersecting) {
      const offsetParent = (intersection.target as HTMLElement).offsetParent;
      const scrollTop = offsetParent?.scrollTop;
      if (!this.requestManager.anyPending) {
        this.fetchDataRequest(true).subscribe(() => {
          if (offsetParent && scrollTop) {
            this.$nextTick().then(() => {
              offsetParent.scrollTop = scrollTop;
            });
          }
        });
      }
    }
  }

  /**
   *
   * Fetch new data and missing data
   *
   */
  fetchDataRequest(infinite = false) {
    const query: PaginatedQuery<any> = {
      pageSize: this.pageSize,
      [this.searchBy]: this.searchTerm,
      ...this.sortParams,
    };

    const infiniteLoad = infinite && typeof this.lastResult?.pageNumber === 'number';

    if (infiniteLoad) {
      query.pageNumber = this.lastResult?.pageNumber! + 1;
    } else {
      query.pageNumber = 0;
      this.lastResult = null;
    }
    const requests: Observable<T[]>[] = [];

    // Add single request to any unkown values on first page query
    if (query.pageNumber === 0 && this.unkownValues.length > 0) {
      this.unkownValues.forEach((val) => requests.push(this.singleFetchRequest(val).pipe(map((r) => [r]))));
    }

    requests.push(
      this.fetchRequest(query).pipe(
        map((res) => {
          this.lastResult = res;
          return res.list;
        })
      )
    );

    return this.requestManager.sameOrCancelAndNew('fetchData', forkJoin(requests)).pipe(
      tap((res) => {
        let list: FieldOptionObj<T>[] = infiniteLoad ? [...this.options] : [];

        res.forEach((r) => {
          list.push(...r.map((el) => ({ label: this.getLabelString(el), value: (el as any)[this.valueKey] })));
        });

        this.options = uniqBy(list, 'value');
      })
    );
  }
}
